import type { Locale } from '@/context/I18nContext';

// Import all locale-specific data files
import profileEn from './profile.en.json';
import profileTr from './profile.tr.json';
import profileDe from './profile.de.json';
import educationEn from './education.en.json';
import educationTr from './education.tr.json';
import educationDe from './education.de.json';
import competitionsEn from './competitions.en.json';
import competitionsTr from './competitions.tr.json';
import competitionsDe from './competitions.de.json';
import experienceEn from './experience.en.json';
import experienceTr from './experience.tr.json';
import experienceDe from './experience.de.json';
import projectsEn from './projects.en.json';
import projectsTr from './projects.tr.json';
import projectsDe from './projects.de.json';
import pmStickyNotesEn from './pmStickyNotes.en.json';
import pmStickyNotesTr from './pmStickyNotes.tr.json';
import pmStickyNotesDe from './pmStickyNotes.de.json';
// Import non-localized data (these don't need translation)
import certificationsData from './certifications.json';
import skillsData from './skills.json';

// Type definitions
export type ProfileData = typeof profileEn;
export type EducationData = typeof educationEn;
export type CompetitionsData = typeof competitionsEn;
export type CertificationsData = typeof certificationsData;
export type ExperienceData = typeof experienceEn;
export type ProjectsData = typeof projectsEn;
export type PMStickyNotesData = typeof pmStickyNotesEn;
export type SkillsData = typeof skillsData;

// StickyNote type definition
export interface StickyNoteData {
  id: string;
  type: 'DONE' | 'IN_PROGRESS' | 'BACKLOG';
  title: string;
  content: string;
  color: string;
  x: number;
  y: number;
  rotation: number;
  badge?: string;
  stamp?: string;
}

// Data loaders
export function getProfileData(locale: Locale): ProfileData {
  if (locale === 'tr') return profileTr;
  if (locale === 'de') return profileDe;
  return profileEn;
}

export function getEducationData(locale: Locale): EducationData {
  if (locale === 'tr') return educationTr;
  if (locale === 'de') return educationDe;
  return educationEn;
}

export function getCompetitionsData(locale: Locale): CompetitionsData {
  if (locale === 'tr') return competitionsTr;
  if (locale === 'de') return competitionsDe;
  return competitionsEn;
}

export function getExperienceData(locale: Locale): ExperienceData {
  if (locale === 'tr') return experienceTr;
  if (locale === 'de') return experienceDe;
  return experienceEn;
}

export function getProjectsData(locale: Locale): ProjectsData {
  if (locale === 'tr') return projectsTr;
  if (locale === 'de') return projectsDe;
  return projectsEn;
}

export function getPMStickyNotesData(locale: Locale): StickyNoteData[] {
  let data;
  if (locale === 'tr') data = pmStickyNotesTr;
  else if (locale === 'de') data = pmStickyNotesDe;
  else data = pmStickyNotesEn;
  // Type assertion to ensure type safety
  return data as StickyNoteData[];
}

// Non-localized data (same for all locales)
export function getSkillsData(): SkillsData {
  return skillsData;
}

export function getCertificationsData(): CertificationsData {
  return certificationsData;
}
